import React, {useState} from 'react';
import {setActiveTab} from 'features/userApplication/userApplicationSlice';
import Table from 'components/table/Table';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import {SearchType} from 'types/search';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import TableCell from 'components/table/TableCell';
import DotsMenu from 'components/DotsMenu';
import {useNavigate} from 'react-router-dom';
import useViewerModal from 'hooks/useViewerModal';
import {UserApplication} from 'types/userApplication';
import {UserEvaluation} from 'types/userEvaluation';
import {UserJobRole} from 'types/userJobRole';
import {UserApplicationThunks} from '../userApplication/userApplicationThunks';
import {UserEvaluationThunks} from '../userEvaluation/userEvaluationThunks';
import {UserJobRoleThunks} from '../userJobRole/userJobRoleThunks';
import Skeleton from 'react-loading-skeleton';
import {JobRole} from 'types/jobRole';
import {UserApplicationService} from 'services/userApplicationService';
import StatusBadge from 'components/StatusBadge';
import usePermissions from 'hooks/usePermissions';

type PendingDocument = UserApplication | UserEvaluation | UserJobRole;

const DashboardPendingApprovalDocumentsTable: React.FC<{
  openDrawer: () => void;
  optionsByTab: any;
}> = ({openDrawer, optionsByTab}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocument[]>();
  const [pagy, setPagy] = useState({count: 0, page: 1, pages: 1});
  const loading = useSelector(
    (state: RootState) =>
      state.userApplication.status === 'loading' ||
      state.userEvaluation.status === 'loading' ||
      state.userJobRole.status === 'loading',
  );
  const dispatch = useDispatch<AppDispatch>();
  const activeTab = useSelector(
    (state: RootState) => state.userApplication.activeTab,
  );
  const navigate = useNavigate();
  const {checkPermission} = usePermissions();

  const {AttachmentModal} = useViewerModal<PendingDocument>({
    openDrawer,
    fetchItemThunk: (id: string) => {
      return dispatch(UserApplicationThunks.show(id)).then(response => {
        if (response.payload) return response;
        return dispatch(UserEvaluationThunks.show(id)).then(response => {
          if (response.payload) return response;
          return dispatch(UserJobRoleThunks.show(id));
        });
      });
    },
  });

  interface VisibleColumnsType {
    name: boolean;
    expDate: boolean;
    userName: boolean;
    status: boolean;
    jobRole: boolean;
    supervisor: boolean;
    email: boolean;
  }

  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    expDate: true,
    userName: true,
    status: true,
    jobRole: true,
    supervisor: true,
    email: true,
  });

  const searchByOptions = [
    {label: 'Employee', value: 'user.first_name', operator: 'like'},
    {label: 'Document', value: 'name', operator: 'like'},
  ];

  const columnOptions = [
    {label: 'Employee', value: 'userName', isVisible: visibleColumns.userName},
    {label: 'Document', value: 'name', isVisible: visibleColumns.name},
    {label: 'Exp Date', value: 'expDate', isVisible: visibleColumns.expDate},
    {label: 'Status', value: 'status', isVisible: visibleColumns.status},
    {label: 'Job Role', value: 'jobRole', isVisible: visibleColumns.jobRole},
    {
      label: 'Supervisor',
      value: 'supervisor',
      isVisible: visibleColumns.supervisor,
    },
    {label: 'Email', value: 'email', isVisible: visibleColumns.email},
  ];

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    search.where['status'] = {
      operator: '==',
      value: 'pending_review',
    };
    search.where['document_pending_reviewers.user_id'] = {
      operator: '==',
      value: user?.id as string,
    };

    const applicationsResponse = await UserApplicationService.index(
      page,
      search,
      sortField,
      sortDirection,
      rowsPerPage,
    );

    const evaluationsResponse = await dispatch(
      UserEvaluationThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );

    const jobRolesResponse = await dispatch(
      UserJobRoleThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );

    const combinedData = [
      ...applicationsResponse.data,
      ...(evaluationsResponse.payload?.data || []),
      ...(jobRolesResponse.payload?.data || []),
    ];

    combinedData.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    setPendingDocuments(combinedData);
    setPagy({
      ...applicationsResponse.pagy,
      count:
        applicationsResponse.pagy.count +
        (evaluationsResponse.payload?.pagy.count || 0) +
        (jobRolesResponse.payload?.pagy.count || 0),
    });
  };

  const handleOpenUserPage = (id?: string) => async () => {
    navigate(`/hr/users/${id}`);
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleEdit = (document: PendingDocument) => {
    if (document.documentTemplateSolution?.id) {
      navigate(`/document-admin-fill/${document.documentTemplateSolution.id}`);
    }
  };

  return (
    <>
      <Table
        tabs={['Pending review']}
        activeTab={activeTab}
        fetchData={fetchData}
        setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
        pagy={pagy}
        loading={loading}
        recordsName={'Documents'}
        columns={true}
        blueButton={false}
        columnOptions={columnOptions}
        onColumnToggle={handleColumnToggle}
        searchByOptions={searchByOptions}
        showCounter={true}
        dateRange={true}
        optionsByTab={optionsByTab}>
        <thead>
          <tr>
            {visibleColumns.userName && (
              <TableHeader label="Employee" sortBy="user.first_name" />
            )}
            {visibleColumns.name && (
              <TableHeader label="Document" sortBy="name" />
            )}
            {visibleColumns.status && (
              <TableHeader label="Status" sortBy="status" />
            )}
            {visibleColumns.jobRole && <TableHeader label="Job Role" />}
            {visibleColumns.supervisor && <TableHeader label="Supervisor" />}
            {visibleColumns.email && (
              <TableHeader label="Email" sortBy="user.email" />
            )}
            <TableHeader label="" />
          </tr>
        </thead>
        <tbody>
          {pendingDocuments?.length !== 0 &&
            pendingDocuments?.map((document: PendingDocument) => {
              const userOptions: {label: string; onClick: () => void}[] = [];
              if (checkPermission('update', 'HR')) {
                userOptions.push({
                  label: 'Sign',
                  onClick: () => handleEdit(document),
                });
              }

              if (loading) {
                return (
                  <TableRow key={document.id}>
                    <td colSpan={6}>
                      <Skeleton count={1} height={40} />
                    </td>
                  </TableRow>
                );
              } else {
                return (
                  <TableRow
                    key={document.id}
                    className="border-t border-slate-200 ">
                    {visibleColumns.userName && (
                      <TableCell>
                        <div className=" relative group">
                          <div className="w-full flex items-center gap-x-3">
                            <div className="flex-grow">
                              {checkPermission('update', 'HR') ? (
                                <span
                                  className="text-sm cursor-pointer font-medium underline text-cyan-600 dark:text-neutral-200"
                                  onClick={handleOpenUserPage(
                                    document.user?.id,
                                  )}>
                                  {document.user?.name}
                                </span>
                              ) : (
                                <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                                  {document.user?.name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    )}
                    {visibleColumns.name && (
                      <TableCell>
                        <div className=" relative group">
                          <div className="w-full flex items-center gap-x-3">
                            <div className="flex-grow">
                              {checkPermission('update', 'HR') ? (
                                <span
                                  className="text-sm cursor-pointer font-medium underline text-cyan-600 dark:text-neutral-200"
                                  onClick={() => handleEdit(document)}>
                                  {document.name}
                                </span>
                              ) : (
                                <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                                  {document.name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    )}
                    {visibleColumns.status && (
                      <TableCell>
                        <StatusBadge status={document.status ?? ''} />
                      </TableCell>
                    )}

                    {visibleColumns.jobRole && (
                      <TableCell>
                        {document.user?.jobRoles
                          .map((role: JobRole) => role.name)
                          .join(', ')}
                      </TableCell>
                    )}
                    {visibleColumns.supervisor && (
                      <TableCell>
                        {document.pendingReviewers
                          ?.map((supervisor: any) => supervisor.name)
                          .join(', ')}
                      </TableCell>
                    )}
                    {visibleColumns.email && (
                      <TableCell>{document.user?.email}</TableCell>
                    )}
                    <TableCell>
                      <DotsMenu options={userOptions} />
                    </TableCell>
                  </TableRow>
                );
              }
            })}
          {pendingDocuments?.length !== undefined &&
            pendingDocuments?.length < 10 &&
            !loading &&
            [...Array(10 - pendingDocuments?.length)].map((_, index) => (
              <TableRow key={index}>
                <td
                  colSpan={
                    Object.entries(visibleColumns).filter(([, value]) => value)
                      .length
                  }
                  className="h-10"></td>
              </TableRow>
            ))}
        </tbody>
      </Table>
      <AttachmentModal />
    </>
  );
};
export default DashboardPendingApprovalDocumentsTable;
